import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './UsersAdmin.css';

const API_BASE = '/api';

const EMPTY_NEW_USER = { name: '', email: '', role: 'salesperson', salesperson_id: '', password: '' };

function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [salespeople, setSalespeople] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resetId, setResetId] = useState(null);
  const [newPw, setNewPw] = useState('');
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState(EMPTY_NEW_USER);
  const [editId, setEditId] = useState(null);
  const [editUser, setEditUser] = useState({ name: '', email: '', role: 'salesperson', salesperson_id: '' });

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = useCallback(async () => {
    try {
      const [u, sp] = await Promise.all([
        axios.get(`${API_BASE}/auth/users`, { headers: headers() }),
        axios.get(`${API_BASE}/auth/salespeople`, { headers: headers() }),
      ]);
      setUsers(u.data.users);
      setSalespeople(sp.data.salespeople);
    } catch (e) { setError('Failed to load users'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const clearMsg = () => { setError(''); setSuccess(''); };

  const doCreate = async () => {
    clearMsg();
    const { name, email, role, salesperson_id, password } = newUser;
    if (!name || !email || !password) { setError('Name, email and password are required.'); return; }
    if (role === 'salesperson' && !salesperson_id) { setError('Pick a salesperson to link.'); return; }
    try {
      const payload = { name, email, role, password, salesperson_id: role === 'salesperson' ? Number(salesperson_id) : null };
      await axios.post(`${API_BASE}/auth/users`, payload, { headers: headers() });
      setSuccess(`User ${email} created.`);
      setNewUser(EMPTY_NEW_USER);
      setCreating(false);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Create failed');
    }
  };

  const startEdit = (u) => {
    clearMsg();
    setResetId(null);
    setEditId(u.id);
    setEditUser({ name: u.name, email: u.email, role: u.role, salesperson_id: u.salesperson_id || '' });
  };

  const doEdit = async (id) => {
    clearMsg();
    const { name, email, role, salesperson_id } = editUser;
    if (!name || !email) { setError('Name and email are required.'); return; }
    if (role === 'salesperson' && !salesperson_id) { setError('Pick a salesperson to link.'); return; }
    try {
      const payload = { name, email, role, salesperson_id: role === 'salesperson' ? Number(salesperson_id) : null };
      await axios.patch(`${API_BASE}/auth/users/${id}`, payload, { headers: headers() });
      setSuccess('User updated.');
      setEditId(null);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Update failed');
    }
  };

  const doToggleActive = async (u) => {
    clearMsg();
    const next = !u.is_active;
    if (!next && !window.confirm(`Deactivate ${u.name}? They will no longer be able to log in.`)) return;
    try {
      await axios.post(`${API_BASE}/auth/users/${u.id}/active`, { is_active: next }, { headers: headers() });
      setSuccess(next ? `${u.name} reactivated.` : `${u.name} deactivated.`);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to update status');
    }
  };

  const doReset = async (id) => {
    clearMsg();
    if (!newPw) { setError('Enter a new password.'); return; }
    try {
      await axios.post(`${API_BASE}/auth/reset-password`, { user_id: id, new_password: newPw }, { headers: headers() });
      setSuccess('Password reset.');
      setResetId(null); setNewPw('');
    } catch (e) {
      setError(e.response?.data?.error || 'Reset failed');
    }
  };

  return (
    <div className="ua-page">
      <h1>Users &amp; Roles</h1>
      <div className="ua-note">
        ℹ️ A person&rsquo;s <strong>name</strong> is just their display label — you can safely edit it
        (e.g. &ldquo;Zen&rdquo; → &ldquo;Zen Ong&rdquo;). Page access follows the <strong>linked salesperson</strong>,
        not the name, so renaming a login won&rsquo;t affect what they can open or do.
      </div>
      {error && <div className="ua-error">{error}</div>}
      {success && <div className="ua-success">{success}</div>}

      <div className="ua-create">
        {creating ? (
          <div className="ua-create-form">
            <input className="ua-input" type="text" placeholder="Name"
              value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} />
            <input className="ua-input" type="email" placeholder="Email"
              value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
            <select className="ua-input" value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value, salesperson_id: '' })}>
              <option value="salesperson">Salesperson</option>
              <option value="admin">Admin</option>
              <option value="marketing">Marketing</option>
              <option value="sales_admin">Sales Admin</option>
              <option value="superadmin">Super Admin</option>
            </select>
            {newUser.role === 'salesperson' && (
              <select className="ua-input" value={newUser.salesperson_id}
                onChange={(e) => setNewUser({ ...newUser, salesperson_id: e.target.value })}>
                <option value="">Link salesperson…</option>
                {salespeople.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
            <input className="ua-input" type="text" placeholder="Password"
              value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
            <button className="ua-btn save" onClick={doCreate}>Create</button>
            <button className="ua-btn" onClick={() => { setCreating(false); setNewUser(EMPTY_NEW_USER); clearMsg(); }}>Cancel</button>
          </div>
        ) : (
          <button className="ua-btn save" onClick={() => { setCreating(true); clearMsg(); }}>+ Create User</button>
        )}
      </div>

      <table className="ua-table">
        <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>
          {users.map(u => (
            <tr key={u.id} className={u.is_active ? '' : 'ua-inactive'}>
              {editId === u.id ? (
                <td colSpan={5}>
                  <div className="ua-create-form">
                    <input className="ua-input" type="text" placeholder="Name"
                      value={editUser.name} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} />
                    <input className="ua-input" type="email" placeholder="Email"
                      value={editUser.email} onChange={(e) => setEditUser({ ...editUser, email: e.target.value })} />
                    <select className="ua-input" value={editUser.role}
                      onChange={(e) => setEditUser({ ...editUser, role: e.target.value, salesperson_id: '' })}>
                      <option value="salesperson">Salesperson</option>
                      <option value="admin">Admin</option>
                      <option value="marketing">Marketing</option>
                      <option value="sales_admin">Sales Admin</option>
                      <option value="superadmin">Super Admin</option>
                    </select>
                    {editUser.role === 'salesperson' && (
                      <select className="ua-input" value={editUser.salesperson_id}
                        onChange={(e) => setEditUser({ ...editUser, salesperson_id: e.target.value })}>
                        <option value="">Link salesperson…</option>
                        {salespeople.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    )}
                    <button className="ua-btn save" onClick={() => doEdit(u.id)}>Save</button>
                    <button className="ua-btn" onClick={() => { setEditId(null); clearMsg(); }}>Cancel</button>
                  </div>
                </td>
              ) : (
                <>
                  <td>{u.name}</td>
                  <td>{u.email}</td>
                  <td>{u.role}</td>
                  <td>
                    <span className={`ua-status ${u.is_active ? 'active' : 'inactive'}`}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    {resetId === u.id ? (
                      <span>
                        <input className="ua-input" type="text" placeholder="new password" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
                        {' '}
                        <button className="ua-btn save" onClick={() => doReset(u.id)}>Save</button>
                        {' '}
                        <button className="ua-btn" onClick={() => { setResetId(null); setNewPw(''); }}>Cancel</button>
                      </span>
                    ) : (
                      <span className="ua-actions">
                        <button className="ua-btn" onClick={() => startEdit(u)}>Edit</button>
                        <button className="ua-btn" onClick={() => doToggleActive(u)}>
                          {u.is_active ? 'Deactivate' : 'Reactivate'}
                        </button>
                        <button className="ua-btn" onClick={() => { setResetId(u.id); setNewPw(''); setEditId(null); clearMsg(); }}>Reset Password</button>
                      </span>
                    )}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default UsersAdmin;
